import { ZoomTransform, zoomIdentity } from 'd3';
import { dispatchAction } from '@ardoq/rxbeach';
import { Rect, type Size, inside } from '@ardoq/graph';
import type { Point } from '@ardoq/math';
import { DisabledZoomControls } from '@ardoq/view-settings';
import { zoomChanged } from './actions';
import {
  BUTTON_CLICK_ZOOM_FACTOR,
  EXPANDER_RADIUS,
  NODE_RADIUS,
  TEXT_NOMINALSIZE,
} from './consts';
import { RelationshipsNode, WindowRect } from './types';
import { absolutePosition } from './visuals/node';
import { getLabelBottomOffset } from './visuals/nodeLabelY';
import { includeAncestors } from 'tabview/graphViews/util';

const generatorForEach =
  <G>(generator: (target: G) => Generator<G>) =>
  (target: G, func: (target: G) => void) => {
    for (const currentTarget of generator(target)) {
      func(currentTarget);
    }
  };
const generatorEvery =
  <G>(generator: (target: G) => Generator<G>) =>
  (target: G, predicate: (target: G) => boolean) => {
    for (const currentTarget of generator(target)) {
      if (!predicate(currentTarget)) {
        return false;
      }
    }
    return true;
  };

export const ancestorsIncludes = (
  node: RelationshipsNode,
  value: RelationshipsNode
) => {
  for (const currentNode of includeAncestors(node)) {
    if (currentNode === value) {
      return true;
    }
  }
  return false;
};

export const ancestorsForEach = generatorForEach(includeAncestors);
export const everyAncestor = generatorEvery(includeAncestors);

export function* includeAllDescendants(
  node: RelationshipsNode
): Generator<RelationshipsNode> {
  yield node;
  if (!node.children) {
    return;
  }
  for (const child of node.children) {
    yield* includeAllDescendants(child);
  }
}

export const findDescendant = (
  node: RelationshipsNode,
  predicate: (node: RelationshipsNode) => boolean
) => {
  for (const currentNode of includeAllDescendants(node)) {
    if (predicate(currentNode)) {
      return currentNode;
    }
  }
  return null;
};

export const descendantsForEach = generatorForEach(includeAllDescendants);
export const everyDescendant = generatorEvery(includeAllDescendants);

export const isAncestor = (
  descendant: RelationshipsNode,
  ancestor: RelationshipsNode
): boolean =>
  descendant.parent
    ? descendant.parent === ancestor || isAncestor(descendant.parent, ancestor)
    : false;

export const getNodeAABB = (node: RelationshipsNode): number[] | null => {
  let aabb: number[] = [0, 0, 0, 0];

  const doit = (
    { r, isSynthetic, children }: RelationshipsNode,
    tx: number,
    ty: number
  ) => {
    if (isSynthetic && children) {
      if (
        !aabb ||
        tx - r < aabb[0] ||
        ty - r < aabb[1] ||
        tx + r > aabb[2] ||
        ty + r > aabb[3]
      ) {
        children.forEach(child => doit(child, tx + child.x, ty + child.y));
      }
    }

    if (!isSynthetic) {
      if (aabb) {
        aabb[0] = Math.min(aabb[0], tx - r);
        aabb[1] = Math.min(aabb[1], ty - r);
        aabb[2] = Math.max(aabb[2], tx + r);
        aabb[3] = Math.max(aabb[3], ty + r);
      } else {
        aabb = [tx - r, ty - r, tx + r, ty + r];
      }

      if (children) {
        aabb[3] += EXPANDER_RADIUS;
      }
    }
  };

  doit(node, 0, 0);

  return aabb;
};

/**
 * Minimum padding (in canvas CSS pixels) around the content when fitting.
 */
export const FIT_PADDING = 16;

/**
 * The label bottom offset from the AABB bottom, in canvas pixels.
 * From the rendering logic in nodeLabelY.ts and node.ts:
 *   - nodeLabelY for a plain leaf = node.r + 1 (in node-space)
 *   - label is middle-aligned, so label bottom = nodeLabelY + fontSize/2
 *   - fontSize = TEXT_NOMINALSIZE * scale_fit * (1/scale_fit) = TEXT_NOMINALSIZE (constant pixels)
 *   - label bottom in canvas px = scale * (node.r + 1) + TEXT_NOMINALSIZE/2 + TEXT_NOMINALSIZE/2
 *                                = scale * (node.r + 1) + TEXT_NOMINALSIZE
 *   - AABB bottom in canvas px  = scale * node.r
 *   - so extension below AABB bottom = scale * 1 + TEXT_NOMINALSIZE
 *
 * The scale-dependent part (scale * 1) is handled in getFitNodeTransform.
 * This constant is the fixed pixel portion.
 */
const LABEL_FIXED_PIXELS_BELOW_AABB = TEXT_NOMINALSIZE;

const getFitNodeToCanvasScale = (
  canvasOffsetWidth: number,
  canvasOffsetHeight: number,
  nodeDiameter: number
) =>
  Math.min(
    canvasOffsetWidth / nodeDiameter,
    // label adds 1 node-space unit (bottom gap) + TEXT_NOMINALSIZE fixed pixels
    (canvasOffsetHeight - 2 * FIT_PADDING - LABEL_FIXED_PIXELS_BELOW_AABB) /
      (nodeDiameter + 1)
  );

export const getFitNodeTransform = (
  canvas: HTMLCanvasElement,
  node: RelationshipsNode
) => {
  const bounds = getNodeAABB(node);

  if (!bounds) {
    return zoomIdentity;
  }

  const [left, top, right, bottom] = bounds;
  const [nodeAbsX, nodeAbsY] = absolutePosition(node);

  const aabbLeft = left + nodeAbsX;
  const aabbRight = right + nodeAbsX;
  const aabbTop = top + nodeAbsY;
  const aabbBottom = bottom + nodeAbsY;

  const aabbWidth = aabbRight - aabbLeft;
  // The node-space gap from the AABB bottom to the label top. Matches nodeLabelY.ts.
  // showExpander is false: zoom-to-fit targets the root node which has no visible expander.
  const labelGap = getLabelBottomOffset(node, false);
  const aabbHeightWithLabelGap = aabbBottom - aabbTop + labelGap;

  const { offsetWidth, offsetHeight } = canvas;

  // The label height in canvas pixels is constant (TEXT_NOMINALSIZE) regardless of scale.
  // Solve: scale * aabbHeightWithLabelGap + LABEL_FIXED_PIXELS_BELOW_AABB + 2*FIT_PADDING = offsetHeight
  const scale = Math.min(
    (offsetWidth - 2 * FIT_PADDING) / aabbWidth,
    (offsetHeight - 2 * FIT_PADDING - LABEL_FIXED_PIXELS_BELOW_AABB) /
      aabbHeightWithLabelGap
  );

  // Content bounding box in canvas pixels:
  //   top:    scale * aabbTop + translateY  (= FIT_PADDING)
  //   bottom: scale * (aabbBottom + labelGap) + translateY + TEXT_NOMINALSIZE  (= offsetHeight - FIT_PADDING)
  // Center of content in canvas pixels = offsetHeight / 2, solving for translateY:
  //   translateY = offsetHeight/2 - scale * (aabbTop + aabbBottom + labelGap) / 2 - LABEL_FIXED_PIXELS_BELOW_AABB / 2
  const translateX = offsetWidth / 2 - (scale * (aabbLeft + aabbRight)) / 2;
  const translateY =
    offsetHeight / 2 -
    (scale * (aabbTop + aabbBottom + labelGap)) / 2 -
    LABEL_FIXED_PIXELS_BELOW_AABB / 2;

  return new ZoomTransform(scale, translateX, translateY);
};

export const transformViewCanvas = (
  transform: ZoomTransform,
  viewInstanceId: string
) => {
  const canvasTransform = new ZoomTransform(
    transform.k * devicePixelRatio,
    transform.x * devicePixelRatio,
    transform.y * devicePixelRatio
  );

  dispatchAction(
    zoomChanged({
      transform: canvasTransform,
    }),
    viewInstanceId
  );
};

/**
 * Although it is possible to disable all zoom controls at once, we disable only the one
 * control when reaching a corresponding zoom  limit during a "natural" user flow.
 *
 * This function is intentionally incorrect mathematically (ZOOM_OUT state scale should be
 * multiplied by ZOOM_FACTOR ** -1), because the zoom factor of a button click and a wheel
 * tick aren't the same. To always reach and display the upper zoom limit we set the disabled
 * state one scaleBy(ZOOM_FACTOR) before the limit is reached. Because of the scale size.
 */
export const getNaturalZoomControlsDisabledState = (
  currentScale: number,
  currentZoomScaleExtent: [min: number, max: number]
) => {
  const scaleWithoutDevicePixelRatio = currentScale / devicePixelRatio;
  return scaleWithoutDevicePixelRatio <= currentZoomScaleExtent[0]
    ? DisabledZoomControls.ZOOM_OUT
    : scaleWithoutDevicePixelRatio * BUTTON_CLICK_ZOOM_FACTOR >=
        currentZoomScaleExtent[1]
      ? DisabledZoomControls.ZOOM_IN
      : DisabledZoomControls.NONE;
};

export const getZoomScaleExtent = (
  canvas: HTMLCanvasElement | null,
  rootNode: RelationshipsNode | null
): [min: number, max: number] =>
  canvas && rootNode
    ? zoomScaleExtent(
        { width: canvas.offsetWidth, height: canvas.offsetHeight },
        getFitNodeTransform(canvas, rootNode).k
      )
    : [0, Infinity];

/**
 * @param param0 the canvas offsetWidth and offsetHeight.
 * @param scaleToFit the scale ("k") of the transform to fit the root node.
 */
export const zoomScaleExtent = (
  { width: canvasWidth, height: canvasHeight }: Size,
  scaleToFit: number
): [min: number, max: number] => {
  const newZoomScaleExtent: [number, number] = [
    scaleToFit,
    getFitNodeToCanvasScale(canvasWidth, canvasHeight, NODE_RADIUS * 2),
  ];
  return newZoomScaleExtent;
};

const convertRelationshipsViewPointOrRectToRectObject = (
  pointOrRect: Point | WindowRect
): Rect => {
  const [x, y, bottom = x, right = y] = pointOrRect;
  return {
    left: x,
    top: y,
    right,
    bottom,
    height: bottom - y,
    width: right - x,
  };
};

export const isPointInRect = (point: Point, rect: WindowRect) =>
  inside(
    convertRelationshipsViewPointOrRectToRectObject(point),
    convertRelationshipsViewPointOrRectToRectObject(rect)
  );

type FindPathAndViewportBoundaryIntersectionArgs = {
  currentDistance: number;
  startDistanceLimit: number;
  endDistanceLimit: number;
  pathElement: SVGPathElement;
  currentWindowRect: WindowRect | null;
};
/**
 * Find the distance of the intersection point between the path and the boundaries of the viewport.
 * Returns the intersection DOMpoint and the pahtLength if the intersection point is found
 */
export const findPathAndViewportBoundaryIntersection = ({
  currentDistance,
  startDistanceLimit,
  endDistanceLimit,
  pathElement,
  currentWindowRect,
}: FindPathAndViewportBoundaryIntersectionArgs): {
  intersectionPointDistance: number;
  intersectionPoint: DOMPoint;
} | null => {
  const currentPoint = pathElement?.getPointAtLength(currentDistance);
  if (!currentWindowRect || !currentPoint) {
    return null;
  }

  // Base case: if the point is intersecting with viewport boundaries, return the distance and
  // the intersection point. We round the numbers to decrease the amount of calls for a sub-
  // pixel difference in the label placement. Remove it if needed, it will increase the number
  // of recursive calls.
  const roundedCurrentX = Math.floor(currentPoint.x);
  const roundedCurrentY = Math.floor(currentPoint.y);
  if (
    currentWindowRect
      .map(Math.floor)
      .some(
        viewportBoundary =>
          roundedCurrentX === viewportBoundary ||
          roundedCurrentY === viewportBoundary
      )
  ) {
    return {
      intersectionPointDistance: currentDistance,
      intersectionPoint: currentPoint,
    };
  }

  const isCurrentPointInWindowRect = isPointInRect(
    [currentPoint.x, currentPoint.y],
    currentWindowRect
  );

  // Safety: if the base case is never satisfied (there is no intersection for whatever reason),
  // we will arrive at this condition. This is valid for base case condition checks with rounded
  // and non-rounded numbers
  const isBaseCaseCacnnotBeFound = isCurrentPointInWindowRect
    ? currentDistance === startDistanceLimit
    : currentDistance === endDistanceLimit;

  const findIntersectionArgs = isCurrentPointInWindowRect
    ? // if the current point is within the viewPort - move towards the edge
      {
        currentDistance: (currentDistance + endDistanceLimit) / 2,
        startDistanceLimit: currentDistance,
        endDistanceLimit: endDistanceLimit,
        pathElement,
        currentWindowRect,
      }
    : // if the current point is outside the viewPort - move towards the center
      {
        currentDistance: (currentDistance + startDistanceLimit) / 2,
        startDistanceLimit: startDistanceLimit,
        endDistanceLimit: currentDistance,
        pathElement,
        currentWindowRect,
      };
  return isBaseCaseCacnnotBeFound
    ? null
    : findPathAndViewportBoundaryIntersection(findIntersectionArgs);
};
